import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { seoService } from '../../services/seoService';
import { TrendingUp, Search, FileText, BarChart3, RefreshCw } from 'lucide-react';

import KeywordManagementTable from './components/KeywordManagementTable';
import PageOptimizationPanel from './components/PageOptimizationPanel';
import GoogleAnalyticsMetrics from './components/GoogleAnalyticsMetrics';
import SitemapManager from './components/SitemapManager';
import PerformanceCharts from './components/PerformanceCharts';

const SEOManagementDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await seoService?.getDashboardMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Error loading SEO dashboard:', err);
      setError(err?.message || 'Erreur lors du chargement du tableau de bord SEO');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Chargement du tableau de bord SEO...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center max-w-md">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-800 font-medium mb-2">Erreur</p>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Tableau de bord SEO</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Gestion du référencement et optimisation</p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <RefreshCw size={18} />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-blue-50 text-blue-600 border-blue-200">
                <TrendingUp size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">Trafic organique</h3>
            <p className="text-3xl font-bold text-gray-900">{metrics?.analytics?.organicTraffic}</p>
            <p className="text-xs text-gray-500 mt-1">Visiteurs/jour</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-green-50 text-green-600 border-green-200">
                <Search size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">Mots-clés suivis</h3>
            <p className="text-3xl font-bold text-gray-900">{metrics?.keywords?.total}</p>
            <p className="text-xs text-gray-500 mt-1">{metrics?.keywords?.topRanked} dans le top 10</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-purple-50 text-purple-600 border-purple-200">
                <FileText size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">Pages optimisées</h3>
            <p className="text-3xl font-bold text-gray-900">{metrics?.pages?.total}</p>
            <p className="text-xs text-gray-500 mt-1">Score moyen: {metrics?.pages?.avgScore}/100</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg border bg-orange-50 text-orange-600 border-orange-200">
                <BarChart3 size={24} />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-2">CTR moyen</h3>
            <p className="text-3xl font-bold text-gray-900">{metrics?.analytics?.ctr?.toFixed(2)}%</p>
            <p className="text-xs text-gray-500 mt-1">Position: {metrics?.analytics?.avgPosition?.toFixed(1)}</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'overview' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Vue d'ensemble
            </button>
            <button
              onClick={() => setActiveTab('keywords')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'keywords' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Mots-clés
            </button>
            <button
              onClick={() => setActiveTab('pages')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'pages' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Pages
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'analytics' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('sitemap')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'sitemap' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-green-600'
              }`}
            >
              Sitemap
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <PerformanceCharts analyticsHistory={metrics?.analyticsHistory} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <KeywordManagementTable keywords={metrics?.keywordsList} onRefresh={handleRefresh} />
              <PageOptimizationPanel pages={metrics?.pagesList} onRefresh={handleRefresh} />
            </div>
          </div>
        )}

        {activeTab === 'keywords' && (
          <KeywordManagementTable keywords={metrics?.keywordsList} onRefresh={handleRefresh} fullView />
        )}

        {activeTab === 'pages' && (
          <PageOptimizationPanel pages={metrics?.pagesList} onRefresh={handleRefresh} fullView />
        )}

        {activeTab === 'analytics' && (
          <GoogleAnalyticsMetrics analytics={metrics?.analytics} analyticsHistory={metrics?.analyticsHistory} />
        )}

        {activeTab === 'sitemap' && (
          <SitemapManager sitemap={metrics?.sitemap} onRefresh={handleRefresh} />
        )}
      </div>
    </div>
  );
};

export default SEOManagementDashboard;